/**
 * LiveSavingsTicker — animated ticker showing savings flagged by the demo
 * engines, shown in demo mode and on demo-account dashboards.
 *
 * v35.70 (Jul 2026) — HONESTY REWRITE (founder decision): the old copy said
 * "CARTIE saved customers $X in the last 24 hours", which implied real
 * customer telemetry while the number was a deterministic simulation seed.
 * That contradicted the brand's radical-honesty thesis (graded forecasts,
 * public SOC 2, 3× guarantee). New copy is precise about the source —
 * "flagged in live demo simulations" — and converts the honesty into a CTA
 * for the free audit. When real customer savings telemetry exists, flip
 * this to read from the API and say "saved customers" truthfully.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Sparkles, ArrowRight } from 'lucide-react';

// Deterministic baseline that climbs slowly day-over-day. Reseeded daily so
// the number is stable within a session but feels alive over time.
const dailyBaseline = () => {
  const today = new Date();
  const epochDays = Math.floor(today.getTime() / (1000 * 60 * 60 * 24));
  // Pseudo-random-but-stable seed for the day
  const seed = (epochDays * 2654435761) % 100000;
  // Range: $24k–$54k per day. Grows ~$50 per epoch day (slow trend).
  return 24000 + (seed % 30000) + epochDays * 50;
};

export default function LiveSavingsTicker({ variant = 'banner' }) {
  const [amount, setAmount] = useState(dailyBaseline());

  useEffect(() => {
    // Tick up by $1-$8 every 3 seconds — feels alive without being a slot machine
    const id = setInterval(() => {
      setAmount((prev) => prev + Math.floor(Math.random() * 8) + 1);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  if (variant === 'banner') {
    return (
      <div
        className="border-b border-emerald-500/30 bg-emerald-600 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 shadow-lg"
        data-testid="live-savings-ticker"
        role="status"
        aria-live="polite"
      >
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap text-center">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
          </span>
          <Sparkles className="w-4 h-4 text-white shrink-0" aria-hidden="true" />
          <span className="text-white text-sm font-semibold">
            CARTIE&rsquo;s engines flagged
          </span>
          <span className="font-mono text-base font-extrabold text-white tabular-nums tracking-tight" data-testid="ticker-amount">
            ${amount.toLocaleString()}
          </span>
          <span className="text-white text-sm font-semibold flex items-center gap-1.5">
            of savings in today&rsquo;s demo simulations <TrendingUp className="w-3.5 h-3.5" aria-hidden="true" />
          </span>
          <Link
            to="/free-audit"
            className="text-sm font-bold text-white underline underline-offset-4 decoration-white/50 hover:decoration-white flex items-center gap-1"
            data-testid="ticker-free-audit-link"
          >
            Find yours — free 48h audit <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    );
  }

  // Inline variant — compact pill for dashboards
  return (
    <div
      className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30"
      data-testid="live-savings-ticker-inline"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <span className="text-emerald-700 dark:text-emerald-300 text-xs font-semibold">Demo savings flagged (24h)</span>
      <span className="font-mono text-sm font-bold text-emerald-700 dark:text-emerald-200 tabular-nums">
        ${amount.toLocaleString()}
      </span>
    </div>
  );
}
